var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import appConfig from '../helpers/appConfig';
import { clearAuthToken, nvFetch } from '../helpers/nvFetch';
import { isWeb } from '../helpers/platform';
import * as platformApi from '../helpers/platformApi';
import nvStorage from '../helpers/storage';
import { fetchBranding } from './branding';
import { extensionUnregistering } from './extension';
const initialState = {
    accessToken: nvStorage.get('token'),
    isAuthenticated: false,
    isAuthTested: false,
    isFetchingUser: false,
    error: undefined,
    user: undefined,
};
export const testAuthAndFetchUser = createAsyncThunk('client/testAuthAndFetchUser', (_, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const accessToken = thunkAPI.getState().user.accessToken;
    if (!accessToken) {
        // reject
        throw new Error();
    }
    const fetchRes = yield nvFetch(`/users/me`, {
        token: accessToken,
    });
    const user = (yield fetchRes.json());
    if (!isWeb()) {
        const base64Url = accessToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window
            .atob(base64)
            .split('')
            .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
            .join(''));
        const parsed = JSON.parse(jsonPayload);
        thunkAPI.dispatch(fetchBranding(parsed.rid));
    }
    return user;
}));
export const finaliseLogout = createAsyncThunk('user/finaliseLogout', (_, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const logoutRedirectUrl = (_a = thunkAPI.getState().branding.branding) === null || _a === void 0 ? void 0 : _a.logoutRedirectUrl;
    platformApi.hasLoggedOut();
    if (logoutRedirectUrl) {
        window.location.href = `${logoutRedirectUrl}?rurl=${window.location.origin}`;
    }
    else {
        nvFetch(`/authenticate/logout`, {
            method: 'POST',
        });
    }
}));
export const logout = createAsyncThunk('user/logout', (_, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    nvStorage.remove('token');
    nvStorage.remove('token-remember');
    nvStorage.remove('token-expires');
    nvStorage.remove('selectedCustomerId');
    const { extension } = appConfig;
    thunkAPI.dispatch(extensionUnregistering());
    if (!extension) {
        console.debug('user.js - !extension');
        thunkAPI.dispatch(finaliseLogout());
        return;
    }
    extension.isActive = false;
    extension.once('unregistered', () => {
        console.debug('user.js - extensionOnceUnregistered');
        thunkAPI.dispatch(finaliseLogout());
    });
    extension.clearSubscriptions();
    extension.stop();
}));
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        tokenReceived: (state, action) => {
            const { accessToken } = action.payload;
            state.accessToken = accessToken;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(testAuthAndFetchUser.pending, (state) => {
            state.isAuthTested = false;
        })
            .addCase(testAuthAndFetchUser.fulfilled, (state, action) => {
            const user = action.payload;
            const { id, accessLevel, username, uri, email, firstName, lastName } = user;
            Sentry.configureScope((scope) => {
                scope.setUser({
                    id,
                    email,
                    username,
                    Name: `${firstName} ${lastName}`,
                    'Access Level': accessLevel,
                    URI: uri,
                });
            });
            state.isFetchingUser = false;
            state.user = user;
            state.isAuthTested = true;
            state.isAuthenticated = true;
            state.error = undefined;
        })
            .addCase(testAuthAndFetchUser.rejected, (state) => {
            state.isAuthTested = true;
            clearAuthToken();
        })
            .addCase(finaliseLogout.fulfilled, (state) => {
            state.isAuthenticated = false;
            state.accessToken = undefined;
            state.user = undefined;
        });
    },
});
export const { tokenReceived } = userSlice.actions;
export default userSlice.reducer;
